import type { FedopsLogger } from '../logger'
import type { EditorSDK } from '../types'
import { promiseOrErrorAfter } from '../wait'

interface CreateSafeUserPreferencesParams {
  editorSdkProxy: EditorSDK
  fedopsLogger: FedopsLogger
  timeout?: number
}

export interface SafeUserPreferences {
  get: <K extends string>(
    keys: K[],
    defaultValue: Record<K, unknown>,
  ) => Promise<Record<K, unknown>>
  set: (value: Record<string, unknown>) => Promise<void>
}

export const createSafeUserPreferences = ({
  editorSdkProxy,
  fedopsLogger,
  timeout = 2000,
}: CreateSafeUserPreferencesParams): SafeUserPreferences => {
  return {
    get: async <K extends string>(
      keys: K[],
      defaultValue: Record<K, unknown>,
    ): Promise<Record<K, unknown>> => {
      try {
        const { timeoutId } =
          fedopsLogger.interactionStarted('getUserPreferences')
        const result = await promiseOrErrorAfter({
          promise: editorSdkProxy.userPreferences.site.get(keys),
          timeout,
          error: new Error('getUserPreferences timeout'),
        })
        fedopsLogger.interactionEnded('getUserPreferences', {
          timeoutId,
        })
        return result as Record<K, unknown>
      } catch {
        return defaultValue
      }
    },

    set: async value => {
      try {
        const { timeoutId } =
          fedopsLogger.interactionStarted('setUserPreferences')
        const result = await promiseOrErrorAfter({
          promise: editorSdkProxy.userPreferences.site.set(value),
          timeout,
          error: new Error('setUserPreferences timeout'),
        })
        fedopsLogger.interactionEnded('setUserPreferences', {
          timeoutId,
        })
        return result
      } catch {}
    },
  }
}
